import { Injectable } from '@angular/core';
import { EquipmentActivityModel, EquipmentModel, LocationModel } from 'src/app/core/models/equipment.model';
import { HttpClient } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { BuildingModel } from '../../../../core/models/building.model';
import { BuildingService } from '../building/building.service';
import { map } from 'rxjs/operators';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { BuildingActivityModel } from '../../../../core/models/building-activity.model';
import { ContractModel } from '../../../../core/models/contract.model';
import { EquipementMetrikStatusModel } from '../../../../core/models/equipement-metrik-status.model';
import { HistoriqueEvent } from '../../../../core/base/components/history/history.component';
import { EstablishmentModel } from '../../../../core/models/establishment.model';
import { AuthService } from '../../../../core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class EquipmentService extends BaseService<EquipmentModel> {
  defaultSort = 'createdAt';

  protected _location = new BehaviorSubject<LocationModel[]>(null);
  readonly location$ = this._location.asObservable();
  protected _building = new BehaviorSubject<BuildingModel[]>(null);
  readonly building$ = this._building.asObservable();

  // BehaviorSubject pour stocker les equipments
  private _equipments = new BehaviorSubject<EquipmentModel[]>([]);
  readonly equipments$ = this._equipments.asObservable();

  // BehaviorSubject pour les métadonnées (compteur, état du cache)
  private _equipmentsMetadata = new BehaviorSubject<{
    totalData: number;
    loaded: boolean;
  }>({ totalData: 0, loaded: false });
  readonly equipmentsMetadata$ = this._equipmentsMetadata.asObservable();

  constructor(
    public http: HttpClient,
    public buildingService: BuildingService,
    public authService: AuthService,
  ) {
    super(http, 'equipment', authService);
  }

  checkDependencies(equipment: EquipmentModel) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/checkDependencies/${equipment._id}`, {});
  }

  checkActivityDependencies(equipmentId: string, activityId: string) {
    return this.http.post<ObjectApiInterface<any>>(
      `${this.baseUrlApi}/${equipmentId}/checkActivityDependencies/${activityId}`,
      {},
    );
  }

  findById(id: string): Observable<ObjectApiInterface<EquipmentModel>> {
    return super.findById(id).pipe(
      map((x: ObjectApiInterface<EquipmentModel>) => {
        if (x.data.contactIntern) {
          x.data.contactIntern.fullName = `${x.data.contactIntern?.firstName} ${x.data.contactIntern?.lastName}`;
        }
        return x;
      }),
    );
  }

  update(entity: any): Observable<ObjectApiInterface<EquipmentModel>> {
    return super.update(entity).pipe(
      map((x: ObjectApiInterface<EquipmentModel>) => {
        if (x.data.contactIntern) {
          x.data.contactIntern.fullName = `${x.data.contactIntern?.firstName} ${x.data.contactIntern?.lastName}`;
        }
        return x;
      }),
    );
  }

  getGenericEquipmentRelation(_id): Observable<ObjectApiInterface<EquipmentModel[]>> {
    return this.http.get<ObjectApiInterface<EquipmentModel[]>>(`${this.baseUrlApi}/getGenericEquipmentRelation/${_id}`);
  }

  getBuilding(establishmentId: Array<string>, listOfBuildingId: Array<string>, search = '') {
    return this.buildingService
      .findAllActivesBuildings(null, null, 'label', 'desc', search, { establishmentId, excludedIds: listOfBuildingId })
      .pipe(
        map((res: any) => {
          this._building.next(res.data);
          return res;
        }),
      );
  }

  getEstablishmentRelation(_id): Observable<ObjectApiInterface<EquipmentModel[]>> {
    return this.http.get<ObjectApiInterface<EquipmentModel[]>>(`${this.baseUrlApi}/getEstablishmentRelation/${_id}`);
  }

  createLocation(equipmentId, location) {
    return this.http
      .post<ObjectApiInterface<EquipmentModel>>(`${this.baseUrlApi}/${equipmentId}/createLocation`, location)
      .pipe(
        map((x: any) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  updateLocation(id: string, location: any) {
    return this.http.put<ObjectApiInterface<EquipmentModel>>(`${this.baseUrlApi}/${id}/updateLocation`, location).pipe(
      map((x: any) => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  deleteLocation(equipmentId: string, locationId: any) {
    return this.http
      .delete<ObjectApiInterface<EquipmentModel>>(`${this.baseUrlApi}/${equipmentId}/deleteLocation/${locationId}`)
      .pipe(
        map((x: ObjectApiInterface<EquipmentModel>) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  createAmortization(nbPeriod: number, nbValue: number, startDate: Date) {
    const period = nbPeriod;
    const value = nbValue;

    const endDate = new Date(startDate.getFullYear() + period, startDate.getMonth(), startDate.getDate());
    let nextStartDate = new Date(startDate.getFullYear() + 1, 0, 1);
    let nextEndDate: Date;
    if (startDate.getMonth() === 11 && startDate.getDate() === 31) {
      nextEndDate = new Date(startDate.getFullYear() + 1, 11, 31);
    } else {
      nextEndDate = new Date(startDate.getFullYear(), 11, 31);
    }

    const taux = 100 / period / 100;
    const startRestMonth = (12 - (startDate.getMonth() + 1)) * 30 + (30 - (startDate.getDate() - 1));
    const endRestMonth = startDate.getMonth() * 30 + (startDate.getDate() - 1);

    const amortization = [];
    let accumulatedAmount: any = 0;
    let index = period;

    if (startRestMonth > 0) {
      index = period - 1;
      accumulatedAmount = value * taux * (startRestMonth / 360);
      amortization.push({
        nbOfDay: startRestMonth,
        startDate,
        endDate: nextEndDate,
        accumulatedAmount: +accumulatedAmount.toFixed(2),
        amortizationAmount: +(value * taux * (startRestMonth / 360)).toFixed(2),
        netValue: (nbValue - accumulatedAmount).toFixed(2),
      });
      nextEndDate = new Date(nextEndDate.getFullYear() + 1, 11, 31);
    }

    for (let i = 0; i < index; i++) {
      accumulatedAmount += value * taux * (360 / 360);
      amortization.push({
        nbOfDay: 365,
        startDate: nextStartDate,
        endDate: nextEndDate,
        accumulatedAmount: +accumulatedAmount.toFixed(2),
        amortizationAmount: +(value * taux * (360 / 360)).toFixed(2),
        netValue: (nbValue - accumulatedAmount).toFixed(2),
      });

      // add one year to startDate
      nextStartDate = new Date(nextStartDate.getFullYear() + 1, 0, 1);
      nextEndDate = new Date(nextEndDate.getFullYear() + 1, 11, 31);
    }

    if (endRestMonth !== 360) {
      accumulatedAmount += value * taux * (endRestMonth / 360);
      amortization.push({
        nbOfDay: endRestMonth,
        startDate: nextStartDate,
        endDate,
        accumulatedAmount: +accumulatedAmount.toFixed(2),
        amortizationAmount: +(value * taux * (endRestMonth / 360)).toFixed(2),
        netValue: (nbValue - accumulatedAmount).toFixed(2),
      });
    }

    return amortization;
  }

  createActivity(id: string, activity: any) {
    return this.http
      .post<ObjectApiInterface<EquipmentActivityModel>>(`${this.baseUrlApi}/${id}/createActivity`, activity)
      .pipe(
        map((x: any) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  updateActivity(id: string, activity: any) {
    return this.http
      .put<ObjectApiInterface<EquipmentActivityModel>>(`${this.baseUrlApi}/${id}/updateActivity`, activity)
      .pipe(
        map((x: any) => {
          const index = this._entity.value.activities.findIndex(y => y._id === x.data._id);
          this._entity.value.activities[index] = x.data;
          return x;
        }),
      );
  }

  deleteActivity(id: string, activityId: any) {
    return this.http
      .delete<ObjectApiInterface<BuildingActivityModel>>(`${this.baseUrlApi}/${id}/deleteActivity/${activityId}`)
      .pipe(
        map((x: any) => {
          this._entity.value.activities = this._entity.value.activities.filter(y => y._id !== x.data._id);
          return x;
        }),
      );
  }

  addGenerics(data: any) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/addGenerics`, data).pipe(
      map((x: any) => {
        // this._entity.next(x.data);
        return x;
      }),
    );
  }

  findPerimetersByEstablishments(entity: ContractModel, data: string[]) {
    return this.http
      .post<ObjectApiInterface<EquipmentModel[]>>(`${this.baseUrlApi}/find-by-establishments/${entity?._id}`, { data })
      .pipe(
        map((x: any) => {
          return x.data;
        }),
      );
  }

  findEquipmentsByPrestation(prestationId: string) {
    return this.http
      .post<ObjectApiInterface<EquipmentModel[]>>(`${this.baseUrlApi}/find-by-prestation/${prestationId}`, {})
      .pipe(
        map((x: any) => {
          return x.data;
        }),
      );
  }

  setEndServiceDate(id: string, data: any) {
    return this.http.put<ObjectApiInterface<EquipmentModel>>(`${this.baseUrlApi}/${id}/set-end-service`, data).pipe(
      map((x: any) => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  changeAutoDependenciesAndArchive(equipmentId: string): Observable<ObjectApiInterface<EquipmentModel>> {
    return this.http
      .post<
        ObjectApiInterface<EquipmentModel>
      >(`${this.baseUrlApi}/${equipmentId}/change-auto-dependencies-and-archive`, {})
      .pipe(
        map((x: any) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  transformEquipmentData(data: EquipmentModel[]) {
    const transformedData = [];

    // Vérifier que data existe et est un tableau
    if (!data || !Array.isArray(data)) {
      return [];
    }

    for (const equipment of data) {
      // Vérifier que equipment existe et a les propriétés requises
      if (!equipment || !equipment._id || !equipment.establishment) {
        continue;
      }

      const item = {
        _id: equipment._id,
        equipmentId: equipment._id,
        label: equipment.reference || '', // Valeur par défaut si reference est undefined
        genericLabel: equipment.genericEquipment?.label || '', // Optional chaining
        genericId: equipment.genericEquipment?._id || null,
        establishmentId: equipment.establishment._id,
        establishmentLabel: equipment.establishment.label || '',
      };

      if (!equipment.location || !equipment.location.building) {
        transformedData.push({ ...item, buildingId: null, buildingLabel: null });
      } else {
        const transformedItem = {
          ...item,
          buildingId: equipment.location.building._id,
          buildingLabel: equipment.location.building.label || '',
        };
        transformedData.push(transformedItem);
      }
    }

    transformedData.sort((a, b) => {
      // place buildingId null at bottom of the list
      if (a.buildingId === null && b.buildingId !== null) {
        return 1;
      }
      if (a.buildingId !== null && b.buildingId === null) {
        return -1;
      }

      return a.label.localeCompare(b.label);
    });

    return transformedData;
  }

  /**
   * Appel pour créer une metrik.
   * Endpoint: PUT /createMetrik/:_id
   */
  createMetrik(
    equipmentId: string,
    metrik: EquipementMetrikStatusModel,
  ): Observable<ObjectApiInterface<EquipmentModel>> {
    return this.http
      .put<ObjectApiInterface<EquipmentModel>>(`${this.baseUrlApi}/createMetrik/${equipmentId}`, metrik)
      .pipe(
        map(response => {
          this._entity.next(response.data);
          return response;
        }),
      );
  }

  // Override template download to include reference sheets
  downloadExcelTemplate(): Observable<ObjectApiInterface<any>> {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/download-excel-template-with-sheets`);
  }

  /**
   * Charge les équipements ET les stocke dans le BehaviorSubject (cache)
   */
  loadEquipments(
    page?: number,
    perPage?: number,
    sortField?: string,
    sortOrder?: string,
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<EquipmentModel[]>> {
    return this.findAll(page, perPage, sortField, sortOrder, search, filters).pipe(
      tap(res => {
        // Stocker dans le BehaviorSubject
        this._equipments.next(res.data);
        this._equipmentsMetadata.next({
          totalData: res.totalData,
          loaded: true,
        });
      }),
    );
  }

  /**
   * Vide le cache des équipements
   */
  clearCache(): void {
    this._equipments.next([]);
    this._equipmentsMetadata.next({ totalData: 0, loaded: false });
  }
}
